import type { QueryKey } from '@tanstack/react-query';

import useInfiniteQuery from '../../../ReactQuery/useInfiniteQuery';
import { get } from '../../../redux/api';
import type { GetAddableProjectsAndOrReleaseGroupsResponse } from '../../../../shared/types/routes/teams';

/**
 * Generate the query key for finding addable members for a team.
 */
export const getAddableTeamProjectsAndReleaseGroupsQueryKey = (
  teamId: number,
  pageSize: number,
  search?: string,
): QueryKey => ['teams', teamId, 'addable-projects-and-release-groups', pageSize, search];

export const getManualPaginatedAddableTeamProjectsAndReleaseGroupsQueryKey = (
  teamId: number,
  pageSize: number,
  page: number,
  search?: string,
): QueryKey => ['teams', teamId, 'addable-projects-and-release-groups', pageSize, page, search];

/**
 * Fetch the addable Projects + RGs for a team.
 */
export function fetchAddableTeamProjectsAndReleaseGroups(
  { pageParam = 0 },
  teamId: number,
  pageSize: number,
  search?: string,
) {
  return get<GetAddableProjectsAndOrReleaseGroupsResponse>(
    `/api/teams/${teamId}/addable-projects-and-release-groups`,
    false,
    {
      pageSize,
      page: pageParam,
      search,
    },
  );
}

/**
 * Helper hook for fetching the matches of a given component in a user's revision.
 * @param teamId - The ID of the team to fetch addable members for.
 * @param pageSize - The number of addable members to fetch per page.
 * @param search - The search query to filter the addable members by.
 * @param shouldFetch - Whether to fetch the addable members or not. Used to prevent fetching until addable members are needed to be displayed.
 * @returns An infinite query for the addable members of the given team.
 */
export default function useAddableTeamProjectsAndReleaseGroups(
  teamId: number,
  pageSize: number,
  search?: string,
  shouldFetch: boolean = true,
) {
  return useInfiniteQuery<GetAddableProjectsAndOrReleaseGroupsResponse>({
    queryKey: getAddableTeamProjectsAndReleaseGroupsQueryKey(teamId, pageSize, search),
    queryFn: (context) => fetchAddableTeamProjectsAndReleaseGroups(context, teamId, pageSize, search),
    enabled: shouldFetch,
  });
}
